'use client'

import Link from 'next/link'
import { CloseButton, Popover, PopoverButton } from '@headlessui/react'
import { useState } from 'react'
import Spinner from '@/shared/components/Spinner'
import NCPopoverPanel from '@/shared/components/NCPopoverPanel'
import type { AccountNavigationLink } from '@/shared/components/navigation/account-navigation'

export type DesktopPortalLinksProps = {
  accountLinks: AccountNavigationLink[] | null
  onLogout: () => Promise<void>
}

export default function DesktopPortalLinks({
  accountLinks,
  onLogout,
}: DesktopPortalLinksProps) {
  if (!accountLinks)
    return (
      <Link
        className="focus-visible:outline-offset-3 flex h-[42px] items-center justify-center rounded-full bg-[#f4f4f6] px-6 text-[15px] font-medium leading-[18px] text-[#0b0b0f] transition-colors duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] hover:bg-[#e8e8eb] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2563eb]"
        href="/login"
      >
        Sign in
      </Link>
    )

  return <AccountMenu links={accountLinks} onLogout={onLogout} />
}

function AccountMenu({
  links,
  onLogout,
}: {
  links: AccountNavigationLink[]
  onLogout: () => Promise<void>
}) {
  const [loading, setLoading] = useState(false)

  return (
    <Popover className="relative">
      <PopoverButton className="focus-visible:outline-offset-3 group flex h-[42px] items-center justify-center gap-1.5 rounded-full bg-[#f4f4f6] px-6 text-[15px] font-medium leading-[18px] text-[#0b0b0f] transition-colors duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] hover:bg-[#e8e8eb] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2563eb]">
        Account
        <svg
          aria-hidden="true"
          viewBox="0 0 10 6"
          className="h-1.5 w-2.5 transition-transform duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] group-data-[open]:rotate-180"
        >
          <path
            d="m1 1 4 4 4-4"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.4"
          />
        </svg>
      </PopoverButton>

      <NCPopoverPanel
        transition
        anchor="bottom end"
        className="z-[70] mt-2 max-h-[min(70vh,560px)] w-[220px] origin-top-right overflow-y-auto overscroll-contain rounded-[18px] border border-[#ececef] bg-white p-2 shadow-[0_18px_50px_rgba(11,11,15,0.12)] transition duration-[280ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] data-[closed]:translate-y-1 data-[closed]:opacity-0"
      >
        {links.map((link) => (
          <AccountMenuLink href={link.href} key={link.href}>
            {link.title}
          </AccountMenuLink>
        ))}
        <div className="my-1 h-px bg-[#ececef]" />
        <button
          type="button"
          disabled={loading}
          onClick={async () => {
            setLoading(true)
            await onLogout()
          }}
          className="flex min-h-10 w-full items-center rounded-xl px-3 text-left text-[14px] font-medium leading-[18px] text-[#c24040] transition-colors duration-[280ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] hover:bg-[#fbeaea] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#c24040] disabled:cursor-wait disabled:opacity-60"
        >
          Sign out
          {loading && (
            <span className="ml-2">
              <Spinner size={4} />
            </span>
          )}
        </button>
      </NCPopoverPanel>
    </Popover>
  )
}

function AccountMenuLink({
  children,
  href,
}: {
  children: string
  href: string
}) {
  return (
    <CloseButton
      as={Link}
      href={href}
      className="flex min-h-10 items-center rounded-xl px-3 text-[14px] font-medium leading-[18px] text-[#0b0b0f] transition-colors duration-[280ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] hover:bg-[#f4f4f6] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#2563eb]"
    >
      {children}
    </CloseButton>
  )
}
